import { timestampDate } from '@bufbuild/protobuf/wkt'
import { ICellRendererParams } from 'ag-grid-community'
import { Clock } from 'lucide-react'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'

import { relativeTimeFormatted } from '@/lib/date/date'
import { cn } from '@/lib/ui/style-helpers'

import type { ControlRow } from '@/pages/control/ag-grid-control/types'

import { Tooltip } from '@/components/lemmala-design-system/tooltip/tooltip'
import { SelectControlStatusBadge } from '@/components/shared/control-status-badge/control-status-badge'

type ControlStatusCellProps = ICellRendererParams<ControlRow> & {
  readOnly: boolean
  hasManagePermissions: boolean
  hideLastCalculatedDate?: boolean
  from?: string
}

export const ControlStatusCell = ({
  data,
  readOnly,
  hasManagePermissions,
  hideLastCalculatedDate: hideLastCalculatedDate = false,
  from,
}: ControlStatusCellProps) => {
  const {
    id,
    title,
    status,
    lastCalculatedAt,
    ongoingAssessmentId,
    latestAssessmentName,
    controlDefinitionId,
  } = data ?? {}

  if (!data) return null

  const lastCalculatedDate = lastCalculatedAt ? timestampDate(lastCalculatedAt) : undefined
  const isActiveAssessment = !!ongoingAssessmentId

  // Badge component (existing)
  const badgeElement = (
    <SelectControlStatusBadge
      readOnly={readOnly || !hasManagePermissions}
      controlId={id!}
      controlTitle={title}
      status={status!}
      assessmentId={ongoingAssessmentId}
      assessmentName={latestAssessmentName}
      lastCalculatedAt={lastCalculatedDate}
      extraTrackingProps={{
        ...(from && { from }),
        ...(controlDefinitionId && { controlDefinitionId }),
      }}
    />
  )

  // Date text element (conditional) - shown when control has been evaluated and timestamp is not hidden
  const dateTextElement =
    !hideLastCalculatedDate && lastCalculatedDate && status !== ControlStatus.NOT_EVALUATED ? (
      <div
        className={cn(
          'flex items-center gap-1 px-2 text-xs leading-normal',
          isActiveAssessment
            ? 'text-main-foreground-secondary'
            : 'text-main-foreground-placeholder',
        )}
      >
        {!isActiveAssessment && <Clock className='size-3' />}
        <span>Evaluated {relativeTimeFormatted(lastCalculatedDate, { compactUnit: true })}</span>
      </div>
    ) : null

  // Tooltip text (conditional) - shown when we have assessment details
  const evaluatedTooltipText = lastCalculatedDate
    ? `Evaluated ${relativeTimeFormatted(lastCalculatedDate, { compactUnit: true })}`
    : undefined
  const assessmentTooltipText =
    lastCalculatedDate && latestAssessmentName
      ? isActiveAssessment
        ? `Last calculated as part of the active ${latestAssessmentName} assessment`
        : `Last calculated as part of a past ${latestAssessmentName} assessment`
      : undefined

  // Main content wrapper - vertical stack
  const content = (
    <div className='flex flex-col gap-0.5'>
      {badgeElement}
      {dateTextElement}
    </div>
  )

  // Always center the content vertically in the cell
  // Wrap with tooltip if we have tooltip text and timestamp is not hidden
  if ((evaluatedTooltipText || assessmentTooltipText) && !hideLastCalculatedDate) {
    return (
      <div className='flex h-full w-full items-center justify-start'>
        <Tooltip trigger={content} asChild side='left'>
          {evaluatedTooltipText}
          {evaluatedTooltipText && assessmentTooltipText && <br />}
          {assessmentTooltipText}
        </Tooltip>
      </div>
    )
  }

  // Otherwise return unwrapped
  return <div className='flex h-full w-full items-center justify-start'>{content}</div>
}
